import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import DOMPurify from 'dompurify';
import { useId, useCallback } from 'react';

import type { PlatformRules, SocialPlatform, AllowedFileTypes, ValidationResult } from './types';

export * from './types';
export * from './eventsTrial';

const SOCIAL_RULES: Record<SocialPlatform, PlatformRules> = {
  Github: {
    hostname: 'github.com',
    minPathSegments: 1, // e.g., github.com/username
    maxLength: 100,
    customMessage: 'GitHub URL must include a username or repo (e.g., github.com/username)',
  },
  Linkedin: {
    hostname: 'linkedin.com',
    minPathSegments: 1, // e.g., linkedin.com/in/username or linkedin.com/company/companyname
    maxLength: 100,
    customMessage: 'LinkedIn URL must include a profile or company path (e.g., linkedin.com/in/username)',
  },
  // Add more platforms here
};

// Memoized function to generate unique IDs
export const useUniqueId = () => {
  const baseId = useId();
  return useCallback(() => `${baseId}-${Math.random().toString(36).substr(2, 9)}`, [baseId]);
};

// Function to calculate the number of days between two dates
export const calculateDaysDifference = (fromDate: string, toDate?: string): number => {
  if (!fromDate || !dayjs(fromDate).isValid()) {
    return 0; // Return 0 if fromDate is invalid or not provided
  }

  const startDate = dayjs(fromDate);
  const endDate = toDate ? dayjs(toDate) : dayjs();
  return Math.abs(startDate.diff(endDate, 'day'));
};

// Function to calculate the number of years from months
export const calculateYearsFromMonths = (months: number, inMonths: boolean = false): number | string => {
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if(inMonths){
    return `${years} years ${remainingMonths} months`;
  }else{
    return years;
  }
}

// Function to calculate the number of months from days
export const calculateMonthsFromDays = (days: number): number => Math.floor(days / 30);

// Function to encode HTML
export const encodeHTML = (html: string): string =>
  html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

// Function to decode HTML
export const decodeHTML = (html: string): string =>
  html
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'");

// Function to validate name
export const validateName = (
  name: string,
  isRequired: boolean = true
): { isValid: boolean; message: string } => {
  const trimmedName = name.trim();

  // If not required and empty, it's valid
  if (!isRequired && trimmedName === '') {
    return {
      isValid: true,
      message: 'Name is valid',
    };
  }

  // Regex pattern with Unicode support for international names
  const nameRegex = /^[\p{L} '-]+$/u;

  // Only apply length check if required
  if (isRequired && (trimmedName.length < 3 || trimmedName.length > 50)) {
    return {
      isValid: false,
      message: 'Name should be between 3 to 50 characters long',
    };
  }

  if (!nameRegex.test(trimmedName)) {
    return {
      isValid: false,
      message: 'Name can only contain letters, spaces, hyphens, and apostrophes',
    };
  }

  return {
    isValid: true,
    message: 'Name is valid',
  };
};


// Function to validate job title
export const validateJobTitle = (jobTitle: string, isRequired: boolean = true): { isValid: boolean; message: string } => {
  // First trim the input
  const trimmedJobTitle = jobTitle;

  //Check for required field
  if (isRequired && isEmpty(trimmedJobTitle)) {
    return {
      isValid: false,
      message: 'Job Title is required',
    };
  }

  // Check length constraints
  if (trimmedJobTitle.length < 3 || trimmedJobTitle.length > 1024) {
    return {
      isValid: false,
      message: 'Job Title should be between 3 to 1024 characters long',
    };
  }

  // Regex pattern to validate job title
  //const jobTitleRegex = /^[\p{L}\d\s\-'/&.,]+$/u;

  // if (!jobTitleRegex.test(trimmedJobTitle)) {
  //   return {
  //     isValid: false,
  //     message: 'Job Title can only contain letters, numbers, spaces, hyphens, apostrophes, slashes, ampersands, periods, and commas',
  //   };
  // }

  return {
    isValid: true,
    message: 'Job Title is valid',
  };
};

// Function to validate email
export const validateEmail = (email: string): { isValid: boolean; message: string } => {
   // Trim whitespace
   const trimmedEmail = email.trim();
   
   // Check length constraints
   if(trimmedEmail.length > 250){
    return {
      isValid: false,
      message: 'Email address cannot be longer than 250 characters',
    };
   }

  // Regular expression for email validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

  if (!emailRegex.test(trimmedEmail)) {
    return {
      isValid: false,
      message: 'Please enter a valid email address',
    };
  }

  return {
    isValid: true,
    message: 'Email is valid',
  };
};

// Function to validate URL
export const validateURL = (url: string): { isValid: boolean; message: string } => {
  try {
    // First try using the URL constructor for basic validation
    new URL(url);

    // Additional regex validation for more specific rules
    const urlRegex =
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?(\?[&=\w]*)?(#[\w-]*)?$/i;

    if (!urlRegex.test(url)) {
      return {
        isValid: false,
        message: 'Please enter a valid URL',
      };
    }

    return {
      isValid: true,
      message: 'URL is valid',
    };
  } catch {
    return {
      isValid: false,
      message: 'Please enter a valid URL',
    };
  }
};

// Function to validate LinkedIn URL
export const validateLinkedInURL = (url: string): { isValid: boolean; message: string } => {
  // Trim whitespace
  const trimmedUrl = url.trim();

  // Check length constraints
  if(trimmedUrl.length > 100){
    return {
      isValid: false,
      message: 'LinkedIn URL cannot be longer than 100 characters',
    };
  }

  // First check if it's a valid URL
  const urlValidation = validateURL(trimmedUrl);
  if (!urlValidation.isValid) {
    return urlValidation;
  }

  try {
    const linkedInUrl = new URL(url);

    // Check if the domain is linkedin.com
    if (!linkedInUrl.hostname.endsWith('linkedin.com')) {
      return {
        isValid: false,
        message: 'Please enter a valid LinkedIn URL',
      };
    }

    // LinkedIn URL patterns
    const linkedInPathRegex = /^\/(in|company|school|showcase)\/[a-zA-Z0-9-]+\/?$/;

    if (!linkedInPathRegex.test(linkedInUrl.pathname)) {
      return {
        isValid: false,
        message: 'Please enter a valid LinkedIn profile, company, school, or showcase URL',
      };
    }

    return {
      isValid: true,
      message: 'LinkedIn URL is valid',
    };
  } catch {
    return {
      isValid: false,
      message: 'Please enter a valid LinkedIn URL',
    };
  }
};

//Function to validate organisation name
export const validateOrganisationName = (
  name: string,
  isRequired: boolean = true
): { isValid: boolean; message: string } => {
  const trimmedName = name.trim();
  const lengthInvalid = trimmedName.length < 4 || trimmedName.length > 256;

  if (isEmpty(trimmedName)) {
    if (isRequired) {
      return { isValid: false, message: "Organisation Name is required" };
    }
    return { isValid: true, message: "Organisation Name is valid" }; // optional and empty is fine
  }

  if (lengthInvalid) {
    return {
      isValid: false,
      message: "Organisation Name should be between 4 to 256 characters long",
    };
  }

  return { isValid: true, message: "Organisation Name is valid" };
};


// Function to sanitize input based on type
export const sanitizeInput = (
  value: string | File,
  type: 'name' | 'email' | 'url' | 'linkedin_url' | 'file' | 'jobTitle' | 'organisationSlug' | 'linkedin_profile' | 'organisationName'
): string => {
  let sanitized = null;

  if (type === 'file') {
    sanitized = (value as File).name.trim();
  } else {
    sanitized = (value as string).trim();
  }

  switch (type) {
    case 'name':
      sanitized = sanitized
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .replace(/[^A-Za-zÀ-ÿ\u00f1\u00d1\u00e9\u00c9\s'-]/g, ''); // Allow letters, accents, spaces, hyphen, apostrophe
      break;
    case 'jobTitle':
      sanitized = DOMPurify.sanitize(sanitized);
      break;

    case 'email':
      sanitized = DOMPurify.sanitize(sanitized);
      break;
    case 'organisationSlug':
      sanitized = DOMPurify.sanitize(sanitized);
      break;

    case 'organisationName':
      sanitized = DOMPurify.sanitize(sanitized);
      break;

    case 'url':
    case 'linkedin_url':
      sanitized = sanitized
        .replace(/\s+/g, '') // Remove whitespace
        .trim();
      break;
    
    case 'linkedin_profile':
       sanitized = DOMPurify.sanitize(sanitized);
       break;

    case 'file':
      sanitized = sanitized
        .trim()
        .replace(/[^\w.\- ]/g, '') // Remove invalid characters
        .replace(/\s+/g, '_'); // Replace spaces with underscores
      break;
    default:
      break;
  }

  return sanitized;
};

// Combined sanitize + validate function
export const sanitizeAndValidate = (
  value: string | File,
  type: 'name' | 'email' | 'url' | 'linkedin_url' | 'file' | 'jobTitle' | 'organisationSlug' | 'linkedin_profile' | 'organisationName',
  isRequired: boolean = true,
  allowedFileTypes?: AllowedFileTypes[],
  maxSizeInMB?: number
): { sanitized: string; isValid: boolean; message: string } => {
  const sanitized = sanitizeInput(value, type);
  let validationResult;

  switch (type) {
    case 'name':
      validationResult = validateName(sanitized, isRequired);
      break;
    case 'jobTitle':
      validationResult = validateJobTitle(sanitized, isRequired);
      break;
    case 'email':
      validationResult = validateEmail(sanitized);
      break;
    case 'url':
      validationResult = validateURL(sanitized);
      break;
    case 'linkedin_url':
      validationResult = validateLinkedInURL(sanitized);
      break;
    case 'linkedin_profile':
      validationResult = validateLinkedInProfileInput(sanitized);
      break;
    case 'organisationName':
      validationResult = validateOrganisationName(sanitized);
      break;
    case 'organisationSlug':
      validationResult = validateOrganisationSlug(sanitized);
      break;
    case 'file':
      validationResult = validateFile(value as File, allowedFileTypes, maxSizeInMB);
      break;
    default:
      validationResult = {
        sanitized,
        isValid: false,
        message: 'Invalid input type',
      };
      break;
  }

  return {
    sanitized,
    isValid: validationResult.isValid,
    message: validationResult.message,
  };
};

// Function to validate file
export const validateFile = (
  file: File,
  allowedFileTypes: AllowedFileTypes[] = ['pdf', 'doc'],
  maxSizeInMB = 2
): { isValid: boolean; message: string } => {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

  const allowedMimeMap: Record<AllowedFileTypes, string[]> = {
    image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    pdf: ['application/pdf'],
    doc: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    zip: ['application/zip', 'application/x-zip-compressed'],
  };

  // 1. Empty check
  if (file.size === 0) {
    return { isValid: false, message: 'File is empty' };
  }

  // 2. Size check
  if (file.size > maxSizeInBytes) {
    return { isValid: false, message: `File exceeds ${maxSizeInMB}MB` };
  }

  // 3. Type check
  const allAllowedMimes = allowedFileTypes.flatMap((type) => allowedMimeMap[type]);
  if (!allAllowedMimes.includes(file.type)) {
    return { isValid: false, message: 'Invalid file type' };
  }

  // 4. Extension & MIME match
  const extension = file.name.split('.').pop()?.toLowerCase();
  const mime = file.type;

  if (
    (mime.startsWith('image/') &&
      !['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) ||
    (mime === 'application/pdf' && extension !== 'pdf') ||
    (mime.includes('word') && !['doc', 'docx'].includes(extension || '')) ||
    (allowedMimeMap.zip.includes(mime) && extension !== 'zip')
  ) {
    return { isValid: false, message: 'Extension does not match file type' };
  }

  return { isValid: true, message: 'File is valid' };
};

/**
 * Sanitizes input string to prevent XSS attacks.
 * @param input - Any string input from user
 * @returns Safe string
 */
export function sanitizeCommonInput(input: string): string {
  return DOMPurify.sanitize(input.trim());
}

export const validateSocialURL = (
  url: string,
  platform: SocialPlatform
): ValidationResult => {
  const trimmedUrl = url.trim();

  const rules = SOCIAL_RULES[platform];
  if (!rules) {
    return {
      isValid: false,
      message: `Validation rules for ${platform} are not defined.`,
    };
  }

  if (rules.maxLength && trimmedUrl.length > rules.maxLength) {
    return {
      isValid: false,
      message: `${platform} URL cannot be longer than ${rules.maxLength} characters`,
    };
  }

  const normalizedUrl = trimmedUrl.startsWith('http')
    ? trimmedUrl
    : `https://${trimmedUrl}`;

  try {
    const parsedUrl = new URL(normalizedUrl);

    if (!parsedUrl.hostname.endsWith(rules.hostname)) {
      return {
        isValid: false,
        message: `Please enter a valid ${platform} URL (must include ${rules.hostname})`,
      };
    }

    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    if (pathSegments.length < rules.minPathSegments) {
      return {
        isValid: false,
        message: rules.customMessage || `Please enter a valid ${platform} URL`,
      };
    }

    return {
      isValid: true,
      message: `${platform} URL is valid`,
    };
  } catch {
    return {
      isValid: false,
      message: `Please enter a valid ${platform} URL`,
    };
  }
};

//Function to append redirect path to the URL
export const createRedirectPath = (currentPath: string, pathname: string) => {
  const queryString = new URLSearchParams({ returnTo: pathname }).toString();
  return `${currentPath}?${queryString}`;
};

//Function to format Location details
export const formatLocationDetails = (locationDetails: { LocationCountry: string, LocationCity: string, LocationState: string, LocationZipCode: string }) => {
  const { LocationCountry, LocationCity, LocationState, LocationZipCode } = locationDetails;
  const location = [];
  if(LocationCity && !isEmpty(LocationCity)) location.push(LocationCity);
  if(LocationState && !isEmpty(LocationState)) location.push(LocationState);
  if(LocationCountry && !isEmpty(LocationCountry)) location.push(LocationCountry);
  if(LocationZipCode && !isEmpty(LocationZipCode)) location.push(LocationZipCode);
  return location.join(', ');
}

//Function to format StartDate to EndDate
export const formatStartDateToEndDate = (startDate: string, endDate: string) => {
  let dateString = '';
  if(startDate && endDate){
    dateString = `${startDate} - ${endDate}`;
  }else if(startDate){
    dateString = `${startDate} - Present`;
  }else if(endDate){
    dateString = `${endDate}`;
  }
  return dateString;
}

//Function to return relative time for a Date
export const getRelativeTime = (date: string, threshold: number = 5) => {
  const now = dayjs();
  const inputDate = dayjs(date);
  const diffInDays = inputDate.diff(now, 'day');

  if (Math.abs(diffInDays) <= threshold) {
    return inputDate.fromNow(); // e.g., "in 3 hours", "4 days ago"
  } else {
    return inputDate.format('MMM D, YYYY'); // e.g., "Jul 14, 2025"
  }
}

//Function to highlight the AI Insights in a Para
export const highlightAIKeyWords = (desc: string, color: string = '#9747FF') => {
  const regex = /\*\*([^*]*)\*\*/gm;
  const highlightedDesc = desc?.replace(regex, `<span style="color: ${color}; font-weight: 600;">$1</span>`);
  return DOMPurify.sanitize(highlightedDesc);
}

//Function to validate organisationSlug
export const validateOrganisationSlug = (
  slug: string
): { isValid: boolean; message: string } => {
  const trimmedSlug = slug.trim();

  if (trimmedSlug.length < 4 || trimmedSlug.length > 256) {
    return {
      isValid: false,
      message: 'Organisation Slug should be between 4 to 256 characters long',
    };
  }

  // Only lowercase letters, numbers, single hyphens between words
  const slugRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;

  if (!slugRegex.test(trimmedSlug)) {
    return {
      isValid: false,
      message:
        'Organisation Slug can only contain lowercase letters, numbers, and single hyphens between them. No spaces or consecutive hyphens are allowed.',
    };
  }

  return {
    isValid: true,
    message: 'Organisation Slug is valid',
  };
};

export function isCustomActiveLink(currentPath: string, linkPath: string, { exact = false } = {}) {
  if (!currentPath || !linkPath) return false;

  const cleanCurrent = stripQuery(normalizePath(currentPath)).toLowerCase();
  const cleanLink = stripQuery(normalizePath(linkPath)).toLowerCase();

  if (exact) {
    return cleanCurrent === cleanLink;
  }

  return cleanCurrent === cleanLink || cleanCurrent.startsWith(cleanLink + '/');
}

export function normalizePath(path: string) {
  return path.replace(/\/+$/, '') || '/';
}

export function stripQuery(path: string) {
  return path.split('?')[0].split('#')[0];
}


/**
 * Validates a LinkedIn profile input which can be either:
 * 1. A full LinkedIn profile URL (in loose format, without strict path checks).
 * 2. A LinkedIn profile name/slug (e.g., "john-doe").
 *
 * Rules:
 * - Input must be between 3 and 100 characters.
 * - If input looks like a URL:
 *    - Supports missing "https://" and "www."
 *    - Must point to a "linkedin.com" domain
 *    - No validation on specific path structure like "/in/", "/company/", etc.
 * - If input is a profile name:
 *    - Only letters, numbers, and hyphens allowed
 *    - No special characters or spaces
 *
 * @param input - User-provided LinkedIn profile input
 * @returns An object with `isValid` (boolean) and a `message` string
 */
export const validateLinkedInProfileInput = (
  input: string
): { isValid: boolean; message: string } => {
  const trimmedInput = input.trim();

  // Length check
  if (trimmedInput.length < 3) {
    return {
      isValid: false,
      message: 'Linked Profile must be at least 3 characters long',
    };
  }

  if (trimmedInput.length > 100) {
    return {
      isValid: false,
      message: 'Linked Profile cannot be longer than 100 characters',
    };
  }

  // Check if it looks like a URL
  const isLikelyUrl = /^[a-zA-Z]+:\/\//.test(trimmedInput) || trimmedInput.includes('.') || trimmedInput.includes('/');

  if (isLikelyUrl) {
     // Remove query params (everything after `?`)
     let cleanedInput = trimmedInput.split('?')[0];

     // Normalize: Add https and www if missing
     if (!/^https?:\/\//i.test(cleanedInput)) {
       cleanedInput = 'https://' + cleanedInput;
     }
 
     if (!/^https?:\/\/(www\.)?/i.test(cleanedInput)) {
       cleanedInput = cleanedInput.replace(/^https?:\/\//i, 'https://www.');
     }

    try {
      const parsed = new URL(cleanedInput);

      // Reject if query string exists (to be extra safe)
      if (parsed.search) {
        return {
          isValid: false,
          message: 'Query parameters are not allowed in LinkedIn URLs',
        };
      }

      // Hostname must contain linkedin.com
      if (!parsed.hostname.includes('linkedin.com')) {
        return {
          isValid: false,
          message: 'URL must point to linkedin.com',
        };
      }

      return {
        isValid: true,
        message: 'Valid LinkedIn profile URL',
      };
    } catch {
      return {
        isValid: false,
        message: 'Invalid LinkedIn URL format',
      };
    }
  } else {
    // Treat as a profile name/slug
    const profileSlugRegex = /^[a-zA-Z0-9-]{3,100}$/;

    if (!profileSlugRegex.test(trimmedInput)) {
      return {
        isValid: false,
        message: 'Profile name can only contain letters, numbers, and hyphens',
      };
    }

    return {
      isValid: true,
      message: 'Valid LinkedIn profile name',
    };
  }
};

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const cleanQueryParams = (url: string) => url.split('?')[0]

export const normalize = (html: string): string => {
  if (!html) return "";

  // Parse into DOM
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Get text content only (ignores tags, formatting, &nbsp;)
  let text = doc.body.textContent || "";

  // Collapse whitespace, trim
  text = text.replace(/\s+/g, " ").trim();

  return text;
}

export function getInitials(displayName: string): string {
  if (!displayName) return '?';

  // Split by space and filter out empty parts
  const nameParts = displayName.trim().split(/\s+/);

  // Take the first letter of first and last part
  const initials = nameParts
    .map(part => part[0].toUpperCase())
    .slice(0, 2) // Only first two letters
    .join('');

  return initials;
}

export function prepareUser(
  users: any[],
  userId: string,
  setUser: (user: any) => void
) {
  if (!users?.length || !userId) return () => {};

  const idleId = requestIdleCallback(() => {
    const matchedUser = users.find((u: any) => u.Id === userId);
    if (matchedUser) setUser(matchedUser);
  });

  // Return cleanup to cancel callback if not yet executed
  return () => cancelIdleCallback(idleId);
}

export function prepareCommentHtml(
  encodedHtml: string,
  users: any[],
  setContent: (html: string) => void
) {
  const decodedHtml = decodeHTML(encodedHtml);

  // Schedule mention replacement when browser is idle
  const idleCallback = window.requestIdleCallback || ((fn: () => void) => setTimeout(fn, 200));

  const handle = idleCallback(() => {
    const updatedHtml = replaceMentionNames(decodedHtml, users);
    setContent(updatedHtml);
  });

  // Cleanup (in case component unmounts before idle time)
  return () => {
    if ("cancelIdleCallback" in window) {
      window.cancelIdleCallback(handle as number);
    } else {
      clearTimeout(handle as number);
    }
  };
}

export function replaceMentionNames(html: string, users: any[]): string {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const mentions = doc.querySelectorAll('span.mention');

  let user: any = {};

  mentions.forEach((span) => {
    const id = span.getAttribute('data-id');
    if (!id) return;

    user = users.find((u: any) => u.Id === id);

    if (user) {
      // Update visible text and label, but retain all styling and attributes
      span.textContent = `@${user?.DisplayName}`;
      span.setAttribute('data-label', user?.DisplayName);
    } else {
      span.textContent = '@Unknown User';
      span.setAttribute('data-label', 'Unknown User');
    }
  });

  return doc.body.innerHTML;
}

